import { RequireAgent } from "@procurement-agents/auth";
import { Badge } from "@procurement-agents/ui";
import { AgentDraftConflict, ApiError, isFullConfig, type AgentConfigAny, type AgentConfigView, type AgentCores, type AgentPatchOp, type AgentsOverview, type ScopeApi } from "@procurement-agents/scope-api";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { NavLink, Route, Routes, useLocation } from "react-router-dom";
import { ScopeApiProvider, useScopeApi } from "../api";
import { relativeTime } from "../labels";
import { AgentCard, AgentsDirectory } from "./AgentCards";
import { History } from "./History";
import { Pipeline } from "./Pipeline";
import { Preview } from "./Preview";
import { Publish } from "./Publish";
import { Workflows } from "./Workflows";

/** Workflow and agent configuration (SPEC_SLICE6 section 8), mounted by the shell under /agents/*: the workflow list,
 * then one workflow's Pipeline, Agents, Preview, Publish and History. */
export function AgentsApp({ api }: { api?: ScopeApi }) {
  return (
    <RequireAgent id="scope">
      <ScopeApiProvider api={api}><AgentsPage /></ScopeApiProvider>
    </RequireAgent>
  );
}

export interface AgentsCtx {
  overview: AgentsOverview;
  /** The draft when the viewer may edit it (configurators), else null. */
  draft: AgentConfigView | null;
  /** What the viewer is looking at on the read-only pages: the draft for configurators, the published version otherwise. */
  shown: AgentConfigAny;
  editable: boolean;
  saving: boolean;
  cores: AgentCores | null;
  refresh: () => Promise<void>;
  apply: (ops: AgentPatchOp[]) => Promise<AgentConfigView | null>;
  startDraft: () => Promise<void>;
}
const WORKFLOW_PAGES = ["pipeline", "agents", "preview", "publish", "history"] as const;
const Ctx = createContext<AgentsCtx | null>(null);
export function useAgents(): AgentsCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error("useAgents outside AgentsApp");
  return c;
}

/** The pages themselves; exported for tests, which wrap it in StaticScopeApiProvider and a MemoryRouter. */
export function AgentsPage() {
  const { api, toast } = useScopeApi();
  const [overview, setOverview] = useState<AgentsOverview | null | undefined>(undefined);
  const [cores, setCores] = useState<AgentCores | null>(null);
  const [saving, setSaving] = useState(false);
  const etag = useRef("");
  const refresh = useCallback(async () => {
    try { const o = await api.agents(); setOverview(o); if (isFullConfig(o.draft)) etag.current = o.draft.updated_at; }
    catch (e) { setOverview(null); if (e instanceof ApiError && e.status !== 403) toast(e.detail, "warn"); }
  }, [api, toast]);
  useEffect(() => { void refresh(); api.agentCores().then(setCores).catch(() => setCores(null)); }, [refresh, api]);

  const draft = overview && overview.role === "configurator" && isFullConfig(overview.draft) ? overview.draft : null;
  const apply = useCallback(async (ops: AgentPatchOp[]) => {
    if (!draft) return null;
    setSaving(true);
    try {
      const d = await api.patchAgentDraft(ops, etag.current);
      etag.current = d.updated_at;
      setOverview((o) => (o ? { ...o, draft: d } : o));
      return d;
    } catch (e) {
      if (e instanceof AgentDraftConflict) { toast("Someone else changed this draft; reloaded their version.", "warn"); await refresh(); }
      else if (e instanceof ApiError) { toast(e.detail, "warn"); }
      else throw e;
      return null;
    } finally { setSaving(false); }
  }, [api, draft, refresh, toast]);
  const startDraft = useCallback(async () => {
    try { const d = await api.startAgentDraft(); etag.current = d.updated_at; setOverview((o) => (o ? { ...o, draft: d } : o)); if (!d.created) toast("Continuing the existing draft."); }
    catch (e) { toast(e instanceof ApiError ? e.detail : String(e), "warn"); }
  }, [api, toast]);

  const loc = useLocation();
  // the module's mount and whether a workflow is open: the first path segment after the mount names the page
  const segs = loc.pathname.split("/").filter(Boolean);
  const tabAt = segs.findIndex((x, i) => i > 0 && (WORKFLOW_PAGES as readonly string[]).includes(x));
  const base = "/" + segs.slice(0, tabAt === -1 ? segs.length : tabAt).join("/");
  const inWorkflow = tabAt !== -1;
  // SMEs read the draft's overlays while one exists (that is what they comment on); otherwise the published version
  const shown = draft ?? (overview && overview.role === "sme" && overview.draft ? overview.draft : overview?.published);
  const value = useMemo<AgentsCtx | null>(() => overview && shown ? { overview, draft, shown, editable: !!draft, saving, cores, refresh, apply, startDraft } : null, [overview, draft, shown, saving, cores, refresh, apply, startDraft]);
  if (overview === undefined) return <p className="muted">Loading…</p>;
  if (overview === null || !value) return <div className="page"><p className="muted">Agent configuration is not available to you.</p></div>;
  const role = overview.role;
  const pub = overview.published;
  return (
    <Ctx.Provider value={value}>
      <div className="page agents">
        <header className="row between">
          <div>
            <h2>Workflow &amp; agent configuration</h2>
            <p className="muted small">
              Published v{pub.version}{pub.published_at ? ` ${relativeTime(pub.published_at)}` : ""}{pub.published_by ? ` by ${pub.published_by}` : ""}.
              {overview.draft ? <> Draft v{overview.draft.version} started by {overview.draft.created_by}{overview.draft.updated_by ? `, last edited by ${overview.draft.updated_by}` : ""}.</> : " No draft."}
              {role === "procurement" && " You see the published configuration; drafts are edited by agent configurators."}
              {role === "sme" && (overview.draft ? " You are reading the draft's drafting and QA instructions and may comment on them." : " You see the published drafting and QA instructions.")}
              {saving && " Saving…"}
            </p>
          </div>
          {draft && <Badge tone="info">editing draft v{draft.version}</Badge>}
        </header>
        {inWorkflow && (
          <nav className="tabs" aria-label="Workflow pages">
            <NavLink to={base} end className="workflow-back">← Workflows</NavLink>
            {role !== "sme" && <NavLink to={`${base}/pipeline`}>Pipeline</NavLink>}
            <NavLink to={`${base}/agents`}>Agents</NavLink>
            {role === "configurator" && <NavLink to={`${base}/preview`}>Preview</NavLink>}
            {role === "configurator" && <NavLink to={`${base}/publish`}>Publish</NavLink>}
            {role !== "sme" && <NavLink to={`${base}/history`}>History</NavLink>}
          </nav>
        )}
        <Routes>
          <Route index element={<Workflows base={base} />} />
          <Route path="pipeline" element={<Pipeline />} />
          <Route path="agents" element={<AgentsDirectory base={base} />} />
          <Route path="agents/:role" element={<AgentCard base={base} />} />
          <Route path="preview" element={<Preview />} />
          <Route path="publish" element={<Publish />} />
          <Route path="history" element={<History />} />
        </Routes>
      </div>
    </Ctx.Provider>
  );
}

/** Configurators without a draft see the published values and one button; everyone else just the values. */
export function DraftGate({ children }: { children?: ReactNode }) {
  const { overview, draft, startDraft } = useAgents();
  if (overview.role !== "configurator" || draft) return <>{children}</>;
  return <p className="draft-gate small"><span className="muted">You are looking at the published configuration.</span> <button className="btn primary" onClick={() => void startDraft()}>Create a new version</button> {children}</p>;
}
